import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { Home, LogOut, CheckCircle, Circle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientTasks() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/portal/tasks`, { withCredentials: true });
      setTasks(res.data);
    } catch (err) {
      if (err.response?.status === 403) navigate('/');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  

  const toggleTask = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await axios.patch(`${API_URL}/api/portal/tasks/${taskId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Task ${newStatus}`);
      fetchTasks();
    } catch {
      toast.error('Failed to update task');
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const filteredTasks = tasks.filter(t => {
    if (filter === 'pending') return t.status !== 'completed';
    if (filter === 'completed') return t.status === 'completed';
    return true;
  });

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="client-tasks-page">
      <header className="bg-[#0F172A] text-white py-4">
        <div className="container-custom flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="DCA" className="h-10 w-auto" />
            <span className="text-lg font-bold">Client Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#94A3B8]">{user?.name}</span>
            <Link to="/" className="text-[#94A3B8] hover:text-white"><Home size={20} /></Link>
            <button onClick={handleLogout} className="text-[#94A3B8] hover:text-white"><LogOut size={20} /></button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b border-[#E2E8F0]">
        <div className="container-custom flex items-center gap-6 py-4 overflow-x-auto">
          <Link to="/portal" className="text-sm font-medium text-[#475569] hover:text-[#1D4ED8] whitespace-nowrap">Dashboard</Link>
          <Link to="/portal/tasks" className="text-sm font-bold text-[#1D4ED8] whitespace-nowrap">My Tasks</Link>
          <Link to="/portal/goals" className="text-sm font-medium text-[#475569] hover:text-[#1D4ED8] whitespace-nowrap">My Plan</Link>
          <Link to="/portal/resources" className="text-sm font-medium text-[#475569] hover:text-[#1D4ED8] whitespace-nowrap">Resources</Link>
          <Link to="/portal/sessions" className="text-sm font-medium text-[#475569] hover:text-[#1D4ED8] whitespace-nowrap">Sessions</Link>
          <Link to="/portal/settings" className="text-sm font-medium text-[#475569] hover:text-[#1D4ED8] whitespace-nowrap">Settings</Link>
        </div>
      </nav>

      <main className="container-custom py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#0A0F1C]" data-testid="tasks-title">My Tasks</h1>
          <div className="flex gap-2">
            {['all', 'pending', 'completed'].map(f => (
              <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 text-sm rounded ${filter === f ? 'bg-[#1D4ED8] text-white' : 'bg-white text-[#475569] border border-[#E2E8F0]'}`} data-testid={`filter-${f}`}>{f}</button>
            ))}
          </div>
        </div>

        {filteredTasks.length > 0 ? (
          <div className="space-y-3">
            {filteredTasks.map((task, i) => (
              <div key={task.task_id} className="bg-white p-4 border border-[#E2E8F0] rounded-lg flex items-center gap-4" data-testid={`task-item-${i}`}>
                <button onClick={() => toggleTask(task.task_id, task.status)} data-testid={`task-toggle-${i}`}>
                  {task.status === 'completed' ? <CheckCircle size={22} className="text-green-600" /> : <Circle size={22} className="text-[#94A3B8]" />}
                </button>
                <div className="flex-grow">
                  <span className={`font-medium ${task.status === 'completed' ? 'text-[#475569] line-through' : 'text-[#0A0F1C]'}`}>{task.title}</span>
                  {task.description && <p className="text-sm text-[#475569] mt-1">{task.description}</p>}
                  {task.due_date && <span className="text-xs text-[#94A3B8]">Due: {task.due_date}</span>}
                </div>
                <span className={`text-xs px-2 py-1 rounded ${task.priority === 'high' ? 'bg-red-100 text-red-700' : task.priority === 'low' ? 'bg-[#F1F5F9] text-[#475569]' : 'bg-[#EFF6FF] text-[#1D4ED8]'}`}>{task.priority}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white p-8 border border-[#E2E8F0] rounded-lg text-center">
            <CheckCircle size={48} className="text-[#E2E8F0] mx-auto mb-4" />
            <p className="text-[#475569]">No tasks found</p>
          </div>
        )}
      </main>
    </div>
  );
}
